import { Typography } from '@workspace/ui/components/Typography';
import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';

import { cn } from '@workspace/ui/lib/utils';
import type React from 'react';
import { Link } from 'react-router-dom';
import {
  cfSrcForImage,
  createCategoryUrl,
} from '~/components/CategoryBlocks/utils';
import type { Category } from '~/stores/CategoriesStore';

import { CategoryBlockHeader } from './shared/CategoryBlockHeader';

interface CategoriesPromoProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
}

export const CategoriesPromo: React.FC<CategoriesPromoProps> = ({
  category,
  className,
  ...props
}) => {
  const { isMobile } = useBreakpoint();

  const categories = category.subcategory.slice(0, 4);

  return (
    <div
      {...props}
      className={cn('mt-6 flex flex-col gap-6 md:gap-8', className)}
    >
      <CategoryBlockHeader
        category={category}
        showViewAll={!isMobile}
        isCategoriesBlock
      />

      <div className="flex gap-4 max-md:flex-col md:flex-wrap">
        {categories.map((category, index) => (
          <CategoryPromoCard
            key={category.id}
            category={category}
            index={index}
          />
        ))}
      </div>
    </div>
  );
};

interface CategoryPromoCardProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Link>, 'to' | 'children'> {
  category: Category;
  index: number;
}

const CategoryPromoCard: React.FC<CategoryPromoCardProps> = ({
  category,
  className,
  index,
  ...props
}) => {
  const { isMobile } = useBreakpoint();

  const desktopPreviewImage = category.subcategory[0]?.previewImage;
  const mobilePreviewImage = category.subcategory[0]?.previewImageThumb;

  const previewImage =
    isMobile && index === 0 ? mobilePreviewImage : desktopPreviewImage;

  return (
    <Link
      {...props}
      to={createCategoryUrl({ id: category.id })}
      className={cn(
        'flex flex-col',
        index === 0
          ? 'aspect-328/381 rounded-6.5 md:aspect-1400/370 md:basis-full'
          : 'aspect-328/202 rounded-6 md:aspect-456/202 md:grow md:basis-[1%]',
        className,
      )}
    >
      {previewImage && (
        <div
          style={{
            backgroundImage: `url(${cfSrcForImage(previewImage)})`,
          }}
          className={cn(
            'z-1 rounded-[inherit] bg-center bg-cover bg-no-repeat',
            index === 0
              ? 'aspect-328/381 md:aspect-1400/370'
              : 'aspect-328/146 md:aspect-456/146',
          )}
        />
      )}
      {index !== 0 && (
        <div className="-mt-10 flex flex-1 items-center justify-center rounded-[inherit] border border-stroke-secondary px-2 pt-12 pb-4">
          <Typography.Text200Regular className="text-center text-text-primary">
            {category.name}
          </Typography.Text200Regular>
        </div>
      )}
    </Link>
  );
};
